/**
 * Walk 4 (#2267) — the short dates and ages the redesigned onboarding pages say.
 *
 * <p>The design speaks in a person's shorthand: "Due 1 Oct", "4 days", "Opened today", "14:32".
 * These helpers say exactly that, and nothing on the pages formats a date any other way. Each one
 * returns honest words for an instant it cannot read, rather than a number derived from NaN.</p>
 *
 * <p>DAYS are counted and DATES are printed in UTC — the same rule the file page's `formatDay`
 * follows, so one file reads the same in two offices. The one exception is a TIME OF DAY
 * ({@link timeOfDay}): "14:32" is only useful in the reader's own clock, so it is the one value
 * rendered in the browser's zone.</p>
 */

/** A day, in milliseconds. */
const DAY_MS = 86_400_000;

function parse(iso: string | null | undefined): Date | null {
  if (iso === null || iso === undefined || iso === "") return null;
  const when = new Date(iso);
  return Number.isNaN(when.getTime()) ? null : when;
}

/** The UTC calendar day of an instant, as a comparable number. */
function utcDay(when: Date): number {
  return Math.floor(when.getTime() / DAY_MS);
}

/**
 * "1 Oct", or "1 Oct 2027" when the date is not in the current UTC year — the year only when it
 * is needed to be unambiguous. `null` for an instant it cannot read.
 */
export function shortDay(iso: string | null | undefined, now: number): string | null {
  const when = parse(iso);
  if (when === null) return null;
  const sameYear = when.getUTCFullYear() === new Date(now).getUTCFullYear();
  return when.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    ...(sameYear ? {} : { year: "numeric" }),
    timeZone: "UTC",
  });
}

/** A due date as a line: "Due 1 Oct", or "Overdue since 1 Oct" once the day has passed. */
export function dueLine(
  iso: string | null | undefined,
  now: number,
): { text: string; overdue: boolean } | null {
  const when = parse(iso);
  if (when === null) return null;
  const day = shortDay(iso, now) as string;
  // Overdue once the due DAY is over, not the instant a midnight timestamp is passed.
  const overdue = utcDay(when) < utcDay(new Date(now));
  return { text: overdue ? `Overdue since ${day}` : `Due ${day}`, overdue };
}

/** How long ago, in a person's words: "today", "1 day", "4 days". `null` when unreadable. */
export function sinceLabel(iso: string | null | undefined, now: number): string | null {
  const when = parse(iso);
  if (when === null) return null;
  const days = Math.max(0, Math.floor((now - when.getTime()) / DAY_MS));
  if (days === 0) return "today";
  return days === 1 ? "1 day" : `${days} days`;
}

/** "Opened today", "Opened yesterday", "Opened 12 days ago". `null` when unreadable. */
export function openedLabel(iso: string | null | undefined, now: number): string | null {
  const when = parse(iso);
  if (when === null) return null;
  const days = Math.max(0, Math.floor((now - when.getTime()) / DAY_MS));
  if (days === 0) return "Opened today";
  if (days === 1) return "Opened yesterday";
  return `Opened ${days} days ago`;
}

/** "14:32", in the reader's own clock. `null` when unreadable. */
export function timeOfDay(iso: string | null | undefined): string | null {
  const when = parse(iso);
  if (when === null) return null;
  return when.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}

/**
 * When a milestone happened, as the history column says it: the time of day for something that
 * happened today, and the short date otherwise.
 */
export function momentLabel(iso: string, now: number): string {
  const when = parse(iso);
  if (when === null) return "not recorded";
  const today = new Date(now);
  const sameLocalDay =
    when.getFullYear() === today.getFullYear() &&
    when.getMonth() === today.getMonth() &&
    when.getDate() === today.getDate();
  return sameLocalDay ? (timeOfDay(iso) as string) : (shortDay(iso, now) as string);
}
